// ============================================================================
// NOTICE TEMPLATE SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { noticeTemplateEndpoints, NOTICE_TEMPLATE_BASE_PATH } from '../endpoints/noticeTemplate.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  NoticeTemplate,
  NoticeTemplateListItem,
  CreateNoticeTemplateRequest,
  UpdateNoticeTemplateRequest,
  NoticeTemplateSearchRequest,
  NoticePreviewRequest,
  NoticePreviewResponse,
} from '../types/noticeTemplate.types';

class NoticeTemplateService {
  async getAll(params?: NoticeTemplateSearchRequest): Promise<PaginatedResponse<NoticeTemplateListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(NOTICE_TEMPLATE_BASE_PATH, params)
      : noticeTemplateEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<NoticeTemplate>> {
    const response = await get<ApiResponse<NoticeTemplate>>(noticeTemplateEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<NoticeTemplate>> {
    const response = await get<ApiResponse<NoticeTemplate>>(noticeTemplateEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateNoticeTemplateRequest): Promise<ApiResponse<NoticeTemplate>> {
    const response = await post<ApiResponse<NoticeTemplate>>(noticeTemplateEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateNoticeTemplateRequest): Promise<ApiResponse<NoticeTemplate>> {
    const response = await put<ApiResponse<NoticeTemplate>>(noticeTemplateEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(noticeTemplateEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${noticeTemplateEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : noticeTemplateEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }

  // Phase 5.7 — render template against a case + recipient
  async preview(body: NoticePreviewRequest): Promise<NoticePreviewResponse> {
    const res = await post<ApiResponse<NoticePreviewResponse>>(noticeTemplateEndpoints.preview(), body);
    return res.data.data!;
  }
}

export const noticeTemplateService = new NoticeTemplateService();
export default noticeTemplateService;
